import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

// Human-friendly short code (avoid ambiguous chars)
const ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
function randomCode(len = 7) {
  let s = "";
  for (let i = 0; i < len; i++) s += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
  return s;
}

export type ChallengeRow = {
  id: string;
  code: string;
  host_name: string | null;
  category: string;
  difficulty: string;
  quiz_ids: string[];
  host_score: number | null;
  host_correct: number | null;
  host_best_streak: number | null;
  created_at: string;
  expires_at: string;
};

export type ChallengeResultRow = {
  id: string;
  challenge_id: string;
  display_name: string;
  score: number;
  correct: number;
  best_streak: number;
  played_at: string;
  is_current_player?: boolean;
};

const PUBLIC_CHALLENGE_COLUMNS = "id,code,host_name,category,difficulty,quiz_ids,host_score,host_correct,host_best_streak,created_at,expires_at";
const PUBLIC_RESULT_COLUMNS = "id,challenge_id,display_name,score,correct,best_streak,played_at";

// -------- Create a challenge (anon-friendly) --------
export const createChallenge = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        category: z.string().min(1).max(32),
        difficulty: z.enum(["beginner", "intermediate", "expert"]),
        quiz_ids: z.array(z.string().uuid()).min(3).max(20),
        host_name: z.string().min(1).max(40).optional(),
        answers: z
          .array(z.object({ option: z.number().int().min(0).max(10).nullable(), speed: z.number().int().min(0).max(3) }))
          .max(20)
          .optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Grade the host's answers on the server; the score to beat is never
    // taken from the browser. Same formula as the game: base 5 + speed (0-3,
    // bounded) + combo bonus (streak capped at 5).
    let host_score: number | null = null, host_correct: number | null = null, host_best_streak: number | null = null;
    if (data.answers && data.answers.length === data.quiz_ids.length) {
      const { data: qs } = await supabaseAdmin.from("quizzes").select("id,answer_index").in("id", data.quiz_ids);
      const key = new Map((qs ?? []).map((q: any) => [q.id, q.answer_index as number]));
      if (key.size === new Set(data.quiz_ids).size) {
        let score = 0, correct = 0, streak = 0, best = 0;
        data.quiz_ids.forEach((id, i) => {
          const a = data.answers![i];
          if (a.option !== null && a.option === key.get(id)) {
            correct++; streak++; best = Math.max(best, streak);
            score += 5 + a.speed + (Math.min(5, streak) - 1) * 2;
          } else streak = 0;
        });
        host_score = score; host_correct = correct; host_best_streak = best;
      }
    }
    // Try up to 5 codes in case of unique collision (very unlikely with 32^7).
    for (let attempt = 0; attempt < 5; attempt++) {
      const code = randomCode(7);
      const { data: row, error } = await supabaseAdmin
        .from("quiz_challenges")
        .insert({
          code,
          category: data.category,
          difficulty: data.difficulty,
          quiz_ids: data.quiz_ids,
          host_name: data.host_name ?? null,
          host_score,
          host_correct,
          host_best_streak,
        })
        .select(PUBLIC_CHALLENGE_COLUMNS)
        .single();
      if (!error && row) return { ok: true as const, challenge: row as ChallengeRow };
      if (error && !String(error.message ?? "").toLowerCase().includes("duplicate")) {
        return { ok: false as const, error: error.message };
      }
    }
    return { ok: false as const, error: "Could not generate a unique code" };
  });

// -------- Load challenge by code + full quiz payload --------
export const getChallenge = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ code: z.string().min(4).max(16) }).parse(d))
  .handler(async ({ data: input }) => {
    let data = input;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: challenge } = await supabaseAdmin
      .from("quiz_challenges")
      .select(PUBLIC_CHALLENGE_COLUMNS)
      .eq("code", data.code.toUpperCase())
      .maybeSingle();
    if (!challenge) return { ok: false as const, error: "not_found" };

    const quizIds = (challenge.quiz_ids as string[]) ?? [];
    const { data: quizzes } = await supabaseAdmin
      .from("quizzes")
      .select("*")
      .in("id", quizIds);

    // Preserve the challenge's original order.
    const byId = new Map((quizzes ?? []).map((q: any) => [q.id, q]));
    const ordered = quizIds.map((id) => byId.get(id)).filter(Boolean);

    const { data: results } = await supabaseAdmin
      .from("quiz_challenge_results")
      .select(PUBLIC_RESULT_COLUMNS)
      .eq("challenge_id", challenge.id)
      .order("score", { ascending: false })
      .limit(50);

    return {
      ok: true as const,
      challenge: challenge as ChallengeRow,
      quizzes: ordered as any[],
      results: (results ?? []) as ChallengeResultRow[],
    };
  });

// -------- Submit a result (anon-friendly) --------
export const submitChallengeResult = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        code: z.string().min(4).max(16),
        player_key: z.string().min(4).max(80),
        display_name: z.string().min(1).max(40),
        score: z.number().int().nonnegative(),
        correct: z.number().int().nonnegative(),
        best_streak: z.number().int().nonnegative(),
      })
      .parse(d),
  )
  .handler(async ({ data: input }) => {
    let data = input;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: challenge } = await supabaseAdmin
      .from("quiz_challenges")
      .select("id,quiz_ids")
      .eq("code", data.code.toUpperCase())
      .maybeSingle();
    if (!challenge) return { ok: false as const, error: "not_found" };
    const qn = ((challenge as any).quiz_ids as string[] | null)?.length ?? 0;
    const correct = Math.min(data.correct, qn);
    data = { ...data, correct, best_streak: Math.min(data.best_streak, correct), score: Math.min(data.score, correct * 16) };

    // Upsert on (challenge_id, player_key): re-playing overwrites their own row.
    const { error } = await supabaseAdmin
      .from("quiz_challenge_results")
      .upsert(
        {
          challenge_id: challenge.id,
          player_key: data.player_key,
          display_name: data.display_name.trim().slice(0, 40),
          score: data.score,
          correct: data.correct,
          best_streak: data.best_streak,
          played_at: new Date().toISOString(),
        },
        { onConflict: "challenge_id,player_key" },
      );
    if (error) return { ok: false as const, error: error.message };

    const { data: results } = await supabaseAdmin
      .from("quiz_challenge_results")
      .select(`${PUBLIC_RESULT_COLUMNS},player_key`)
      .eq("challenge_id", challenge.id)
      .order("score", { ascending: false })
      .limit(50);

    return {
      ok: true as const,
      results: (results ?? []).map(({ player_key, ...result }: any) => ({
        ...result,
        is_current_player: player_key === data.player_key,
      })) as ChallengeResultRow[],
    };
  });
